<script lang="ts">
  import { styleToString } from "@shared/common/node/style";
  import type {
    IFormFileUploaderNode,
    PartialNode,
  } from "@shared/common/node/type";
  import { convertSize, generateFormDataName } from "@shared/common/utils/form";
  import Upload from "lucide-svelte/icons/upload";
  import { onDestroy, onMount } from "svelte";
  import detectReaction from "../../actions/detect-reaction";
  import { getCurrentDocumentNode, getStore } from "../../context";
  import { simpleRandomId } from "../../util/common";
  import FormLabel from "./form/form-label.svelte";

  // 최대 용량 제한(10MB). 필요에 따라 10,000,000(10^7)으로 변경 가능
  const MAX_FILE_SIZE = 10_485_760; // 10 * 1024 * 1024

  const { reactionEventListener, formElement, t } = getStore();

  const documentNode = getCurrentDocumentNode();

  export let node: PartialNode<IFormFileUploaderNode>;

  const id = simpleRandomId();

  $: name = generateFormDataName(node);

  let fileInput: HTMLInputElement | null = null; // input 요소 타입 지정

  function triggerFileInput() {
    if (!fileInput) {
      return;
    }

    fileInput.click();
  }

  // 파일 리스트 관리
  let files: File[] = [];

  // 드래그 오버 상태 관리
  let isDragOver = false;

  // 드래그 영역에 파일이 들어왔을 때
  function handleDragOver(event: DragEvent) {
    event.preventDefault(); // 기본 동작(파일 열기 등) 방지
    isDragOver = true;
  }

  // 드래그 영역에서 벗어났을 때
  function handleDragLeave(event: DragEvent) {
    event.preventDefault();
    isDragOver = false;
  }

  // 드롭 발생 시
  function handleDrop(event: DragEvent) {
    event.preventDefault();
    isDragOver = false;

    if (!event.dataTransfer) return;

    const droppedFiles = Array.from(event.dataTransfer.files);
    handleFileSelection(droppedFiles);
  }

  // 선택된 파일들을 inputElement.files에도 반영하는 함수
  function updateInputFiles(fileList: File[]) {
    if (!fileInput) return;
    const dt = new DataTransfer();
    fileList.forEach((file) => dt.items.add(file));
    fileInput.files = dt.files;
  }

  // 파일 선택(드롭 또는 input[type="file"]) 시 처리
  function handleFileSelection(selectedFiles: File[]) {
    // 최대 파일 개수를 초과하지 않도록 자르기
    const maxCount = node.maxFileCount ?? Infinity;

    // 10MB 초과 파일 걸러내며, 경고(alert)를 띄움
    const filteredFiles = selectedFiles.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        alert(`'${file.name}' ${$t("common.fileMaxSize")}`);
        return false;
      } else if (!file.type) {
        alert(`${$t("common.fileType")}`);
        return false;
      }
      return true;
    });

    // 최대 파일 개수를 초과하는 경우 경고(alert)를 띄움
    if (files.length + filteredFiles.length > maxCount) {
      alert(`${$t("common.fileMaxCount", { max: maxCount })}`);
      updateInputFiles(files);
      return;
    }

    // 이미 업로드된 파일 + 새로 업로드할 파일 합치기
    const newFileList = [...files, ...filteredFiles].slice(0, maxCount);

    files = newFileList;

    // 실제 input 태그에도 반영
    updateInputFiles(files);
  }

  // input[type="file"]에서 파일을 선택했을 때
  function onFileInput(event: Event) {
    const target = event.currentTarget as HTMLInputElement;
    if (!target.files) return;

    const selectedFiles = Array.from(target.files);
    handleFileSelection(selectedFiles);
  }

  // 개별 파일 삭제
  function removeFile(targetFile: File) {
    files = files.filter((file) => file !== targetFile);
    updateInputFiles(files);
  }

  function handleUploadReset() {
    updateInputFiles([]);
    files = [];
  }

  onMount(() => {
    $formElement?.addEventListener("reset", handleUploadReset);
  });

  onDestroy(() => {
    $formElement?.removeEventListener("reset", handleUploadReset);
  });
</script>

<div
  use:detectReaction={{
    node,
    onAction: reactionEventListener,
    documentNode,
  }}
>
  <FormLabel label={node.label} required={node.required} htmlFor={id} />

  <div
    role="button"
    tabindex="0"
    class="file-uploader {isDragOver ? 'drag-over' : ''}"
    on:dragover={handleDragOver}
    on:dragleave={handleDragLeave}
    on:drop={handleDrop}
    on:click={triggerFileInput}
    on:keydown={triggerFileInput}
  >
    <input
      type="file"
      {name}
      multiple
      on:change={onFileInput}
      bind:this={fileInput}
      required={node.required}
      style="position: absolute; pointer-events:none; opacity: 0;"
    />

    <Upload
      style={styleToString({
        width: "calc(var(--cb-form-font-size) * 1.7)",
        height: "calc(var(--cb-form-font-size) * 1.7)",
      })}
    />
    <div>
      <p>첨부할 파일을 여기에 끌어다 놓거나,</p>
      <p>여기를 눌러 파일을 직접 선택해주세요.</p>
    </div>
  </div>
  <div class="file-uploader-header-list">
    <div class="file-uploader-maxcount">
      <span>({fileInput?.files?.length ?? 0}/{node.maxFileCount ?? 1})</span>
    </div>
  </div>
  <div class="file-uploader-list">
    {#if fileInput?.files}
      {#each fileInput.files as file}
        <div class="file-uploader-item">
          <div>
            <span class="file-uploader-item-truncate">
              {file.name.split(".")[0]}
            </span>
            <span class="file-uploader-item-size">
              [{file.name.split(".").pop()}/{convertSize(file.size)}]
            </span>
          </div>
          <button on:click={() => removeFile(file)} type="button">삭제</button>
        </div>
      {/each}
    {/if}
  </div>
</div>

<style>
  .file-uploader {
    border: 1px dashed rgba(var(--cb-form-base-color), 0.7);
    border-radius: 8px;
    background-color: rgba(var(--cb-form-primary-color), 0.01);
    padding: 40px 10px;
    text-align: center;
    color: rgba(var(--cb-form-font-color), 0.7);
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
    font-size: calc(var(--cb-form-font-size) * 1);
    word-break: keep-all;
  }

  .file-uploader:hover {
    border-color: rgba(var(--cb-form-primary-color), 0.7);
    background-color: rgba(var(--cb-form-primary-color), 0.05);
    color: rgba(var(--cb-form-primary-color), 0.6);
  }

  /* 드래그 중에 호버 상태를 표시하기 위한 클래스 */
  .file-uploader.drag-over {
    border-color: rgba(var(--cb-form-primary-color), 0.7);
    background-color: rgba(var(--cb-form-primary-color), 0.05);
  }

  .file-uploader-maxcount {
    font-size: calc(var(--cb-form-font-size) * 1);
    color: rgba(var(--cb-form-font-color), 1);
  }

  .file-uploader-header-list {
    margin-top: 8px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: rgba(var(--cb-form-font-color), 1);
  }

  .file-uploader-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: rgba(var(--cb-form-font-color), 1);
  }

  .file-uploader-item {
    border: 1px solid rgba(var(--cb-form-base-color), 1);
    border-radius: 4px;
    padding: 10px;
    padding-left: 16px;
    font-size: var(--cb-text-md);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .file-uploader-item div {
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 80%;
    gap: 4px;
  }

  .file-uploader-item-truncate {
    white-space: nowrap; /* 텍스트를 한 줄로 유지 */
    overflow: hidden; /* 넘치는 텍스트 숨김 */
    text-overflow: ellipsis; /* 말줄임표(...) 표시 */
    display: inline-block; /* 또는 block, flex 등(폭 지정이 가능한 블록 요소) */
  }

  .file-uploader-item-size {
    flex-shrink: 0;
  }

  button:hover {
    color: rgba(var(--cb-form-font-color), 0.7);
  }
</style>
